import React, { useState, useEffect } from 'react';
import { StageProps } from '../WizardContainer';
import { profileService } from '../../../services/api';
import { DatingProfile, GAMER_STATUS_OPTIONS } from '../../../types/profile';

interface Stage7Props extends StageProps {
  step: number;
}

const Stage7Gaming: React.FC<Stage7Props> = ({
  onNext,
  onBack,
  onSkip,
  isLoading,
  step,
}) => {
  const [profile, setProfile] = useState<Partial<DatingProfile>>({});

  useEffect(() => {
    const loadProfile = async () => {
      const existing = await profileService.getProfile();
      if (existing) {
        setProfile({
          isGamer: existing.isGamer,
          gameLookingForPartners: existing.gameLookingForPartners,
        });
      }
    };
    loadProfile();
  }, []);

  const updateField = (field: keyof DatingProfile, value: unknown) => {
    setProfile((prev: Partial<DatingProfile>) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    await profileService.updateProfile(profile);
    onNext(profile as Record<string, unknown>);
  };

  const handleSkipGaming = () => {
    updateField('isGamer', 'no');
    onSkip();
  };

  const renderStep1 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Gaming Profile</h3>
      <p className="wizard-step-subtitle">
        Connect with gamers who share your passion
      </p>

      <div className="wizard-gaming-optional-banner">
        <span className="optional-icon">💡</span>
        <span className="optional-text">
          <strong>Gaming is completely optional!</strong> You can skip this section and still
          connect with people through dating rooms. GameDate works great for non-gamers too.
        </span>
      </div>

      <div className="wizard-gaming-intro">
        <p>
          If you are a gamer, GameDate is unique because we understand that gaming is more than just a
          hobby - it's a lifestyle. Let us know about your gaming interests!
        </p>
      </div>

      <div className="wizard-form-group">
        <label>Are you a gamer?</label>
        <div className="wizard-options-grid">
          {GAMER_STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.isGamer === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('isGamer', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-skip-section">
        <p>Not a gamer? No problem!</p>
        <button
          type="button"
          className="wizard-btn wizard-btn-text"
          onClick={handleSkipGaming}
        >
          Skip gaming section
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Character Selection</h3>
      <p className="wizard-step-subtitle">
        Choose your in-game character class
      </p>

      <div className="wizard-form-group">
        <p>
          Your character will represent you in the GameDate virtual world.
          You can customize this later in the game settings.
        </p>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Gaming Partner Preferences</h3>

      <div className="wizard-form-group">
        <label>Looking for a gaming partner?</label>
        <div className="wizard-options-grid">
          <button
            type="button"
            className={`wizard-option-btn ${
              profile.gameLookingForPartners === true ? 'selected' : ''
            }`}
            onClick={() => updateField('gameLookingForPartners', true)}
          >
            Yes, let's game together!
          </button>
          <button
            type="button"
            className={`wizard-option-btn ${
              profile.gameLookingForPartners === false ? 'selected' : ''
            }`}
            onClick={() => updateField('gameLookingForPartners', false)}
          >
            Just sharing my interests
          </button>
        </div>
      </div>

      <div className="wizard-completion-message">
        <h4>You're almost done!</h4>
        <p>
          After completing this step, your profile will be live and you can start
          connecting with others.
        </p>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="wizard-stage">
      {renderCurrentStep()}

      <div className="wizard-stage-actions">
        <button
          type="button"
          className="wizard-btn wizard-btn-secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          type="button"
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
          disabled={isLoading}
        >
          {isLoading
            ? 'Saving...'
            : step === 3
            ? 'Complete Profile'
            : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Stage7Gaming;
